import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { paymentsShape, hotelShape, historyShape } from 'constants/propTypes';
import { isEmpty } from 'ramda';
import { useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { useMutation } from '@apollo/client';
import { withRouter } from 'react-router';

import CardSelect from 'components/ui/CardSelect';
import Button from 'components/ui/Button';
import { Row } from 'components/shared/Layout';
import { CurrencyInput } from 'components/ui/Inputs';
import FormattedCurrency from 'components/ui/FormattedCurrency';
import { toDecimalPlaces } from 'utils/calculator';

import { getDisplayForPayment } from './HotelCompensation.utils';
import PriceTable from './PriceTable';
import { AmountLabel, ErrorMessage } from '../../components/ui/Payments/SplitRefundMethods/CardRefund/CardRefund.styles';
import { PROCESS_HOTEL_CARD_REFUND } from 'store/mutations';
import Modal from 'components/ui/Modal';

const HotelCompensationCard = ({ history, reference, onSubmit, hotel, payments, loading }) => {
  const totalPrice = toDecimalPlaces(2, hotel.priceWithMargin);
  const currency = hotel.currency;
  const [processHotelCardRefund, { loading: processHotelCardRefundLoading }] = useMutation(PROCESS_HOTEL_CARD_REFUND);

  const {
    register,
    handleSubmit,
    formState: { isValid },
    watch
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      compensationAmount: 0
    }
  });

  const compensationAmount = watch('compensationAmount');
  const refundAmount = toDecimalPlaces(2, Number(compensationAmount));
  const [maxRefundableAmount, setMaxRefundableAmount] = useState(totalPrice);
  const [refundError, setRefundError] = useState(null);
  const [isOpen, setIsOpen] = useState(false);

  const redirectToExtrasPage = () => {
    history.push(`/manage/bookings/${reference}/extras`);
  };

  const onSubmitForm = async ({ pspReference }) => {
    try {
      await processHotelCardRefund({
        variables: {
          bookingReference: reference,
          pspReference,
          refundAmount,
          currency
        }
      });
    } catch (err) {
      setRefundError(err.message);
      setIsOpen(true);
      return;
    }

    redirectToExtrasPage();
  };

  const onClose = () => {
    setIsOpen(false);
  };

  return (
    <div>
      <Modal isOpen={isOpen} onClose={onClose}>
        <div>{refundError}</div>
        <Button data-cy="button-error-modal" type="button" onClick={redirectToExtrasPage}>
          OK
        </Button>
      </Modal>

      <h2>
        <FormattedMessage id="prrRefund.compensationAmount" />
      </h2>
      <Row>
        <form onSubmit={handleSubmit(onSubmitForm)}>
          <Row>
            <CurrencyInput
              currency={currency}
              dataCy="compensation-amount"
              dataTestId="compensation-amount"
              isControlled={false}
              {...register('compensationAmount', { required: true, min: 0.01, max: totalPrice })}
            />
          </Row>
          <PriceTable currency={currency} totalPrice={totalPrice} refundAmount={refundAmount} />
          <Row>
            <h2>
              <FormattedMessage id="prrRefund.processRefund" />
            </h2>
          </Row>
          <Row>
            <h3>
              <FormattedMessage id="prrRefund.selectCard" />
            </h3>
            <Row data-cy="credit-card-list" span={6}>
              {payments.map((p) => {
                const availableAmount = p.availableRefundAmount || 0;

                return (
                  <CardSelect
                    key={p.id}
                    value={p.reference}
                    id={p.id}
                    testId={p.reference}
                    disabled={currency !== p.currency || availableAmount <= 0}
                    {...register('pspReference', { required: true })}
                    onClick={() => setMaxRefundableAmount(availableAmount > totalPrice ? totalPrice : availableAmount)}
                  >
                    {getDisplayForPayment(p)}
                    <div>
                      <AmountLabel>
                        <FormattedMessage id="refunds.original" />
                      </AmountLabel>
                      <FormattedCurrency
                        dataCy={`hotel-compensation-original-amount-${p.reference}-input`}
                        currency={p.currency}
                        amount={p.amount}
                      />
                    </div>
                    <div>
                      <AmountLabel>
                        <FormattedMessage id="refunds.available" />
                      </AmountLabel>
                      <FormattedCurrency
                        dataCy={`hotel-compensation-available-amount-${p.reference}-input`}
                        currency={p.currency}
                        amount={availableAmount}
                      />
                    </div>
                  </CardSelect>
                );
              })}
            </Row>
          </Row>

          {maxRefundableAmount < refundAmount && maxRefundableAmount === totalPrice && (
            <ErrorMessage data-cy="card-max-total-amount-error-msg">
              <FormattedMessage id="hotelCompensation.maxCompensationError" />{' '}
              <FormattedCurrency currency={currency} amount={maxRefundableAmount} />
            </ErrorMessage>
          )}

          {maxRefundableAmount < refundAmount && maxRefundableAmount < totalPrice && (
            <ErrorMessage data-cy="card-max-amount-error-msg">
              <FormattedMessage id="hotelCompensation.maxCardCompensationError" /> <FormattedMessage id="hotelCompensation.maxAmount" />
              {': '}
              <FormattedCurrency currency={currency} amount={maxRefundableAmount} />
            </ErrorMessage>
          )}

          <Button
            data-cy="submit-hotel-compensation"
            type="submit"
            disabled={!isValid || loading || processHotelCardRefundLoading || isEmpty(payments) || maxRefundableAmount < refundAmount}
            loading={loading || processHotelCardRefundLoading}
          >
            <FormattedMessage id="prrRefund.processRefundCta" />
          </Button>
        </form>
      </Row>
    </div>
  );
};

HotelCompensationCard.propTypes = {
  history: historyShape,
  hotel: hotelShape,
  reference: PropTypes.string,
  onSubmit: PropTypes.func,
  payments: paymentsShape.isRequired,
  loading: PropTypes.bool
};

export default withRouter(HotelCompensationCard);
